// =========================
// Generic Containers Styles
// =========================

import { flexCol, flexRow, flexColumnCenter } from "./flex";
import { spaces, spacesPercent } from "./spacing";
import { colors } from "./colors";
import { zIndexes } from "./zIndexes";

// =========================
// Layout: Basic containers
// =========================

export const containerColumn1 = {
  ...flexCol,
  gap: spaces[1.5],
  padding: spaces[0],
  height: "100%",
};

export const containerColumn2 = {
  ...flexCol,
  gap: spaces[1.5],
  padding: spaces[1],
  height: "98%",
};

export const containerRow1 = {
  ...flexRow,
  gap: spaces[3],
  padding: spaces[0],
};

export const containerRow2 = {
  display: "flex",
  flexDirection: "row",
  alignItems: "stretch",
  justifyContent: "space-between",
  gap: spaces[3],
  flex: 1,
  minHeight: 0,
  height: "auto",
  padding: spaces[1],
};

export const containerFit = {
  padding: spaces[1],
  flex: "1",
  alignItems: "center",
  height: "98%",
};

// =========================
// Paper / sections
// =========================

export const paperColumnContainer = {
  ...flexCol,
  gap: spaces[1],
  padding: spaces["1.5"],
  height: "calc(100vh - 135px)",
};

// =========================
// Empty state
// =========================

export const noContentView = {
  height: "90%",
  ...flexColumnCenter,
  gap: spaces[1],
  color: colors.secondary,
};

export const noContentIcon = {
  fontSize: 80,
  opacity: 0.12,
};

export const noContentText = {
  marginTop: spaces[2],
};

// =========================
// Generic full-size helpers
// =========================

export const fullW = {
  width: spacesPercent[100],
};

export const fullH = {
  height: spacesPercent[100],
};

export const fullWH = {
  width: spacesPercent[100],
  height: spacesPercent[100],
};

// =========================
// Position helpers
// =========================

export const relativeBox = {
  position: "relative",
  width: "100%",
  height: "100%",
};

export const topRightAction = {
  position: "absolute",
  top: spaces[0.5],
  right: spaces[0.5],
  zIndex: zIndexes.z1205,
};

// =========================
// Right actions row
// =========================

export const rightActionsRow = {
  ...flexRow,
  alignItems: "center",
  justifyContent: "flex-end",
  gap: spaces[1],
  width: "100%",
  marginBottom: spaces[1],
};

// =========================
// File upload box styles
// =========================

export const fileUploadBox = {
  border: `2px dashed ${colors.darkGray}`,
  borderRadius: "8px",
  padding: spaces[2],
  textAlign: "center" as const,
  cursor: "pointer",
  backgroundColor: colors.lightGray,
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: colors.primary,
    backgroundColor: colors.background,
  },
};

export const fileUploadIcon = {
  fontSize: "48px",
  color: colors.bluePrimary,
  marginBottom: spaces[0],
};

// =========================
// CheckBoxSelectorTable Styles (Theme-aware)
// =========================

export const checkboxSelectorMenuSx = (mode: "light" | "dark") => ({
  position: "sticky",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10,
  backgroundColor: mode === "dark" ? "#2a2a2a" : "#fff",
  borderBottom: mode === "dark" ? "1px solid #3a3a3a" : "1px solid #eee",
});

export const checkboxSelectorSearchBoxSx = {
  px: 2,
  py: 1,
};

export const checkboxSelectorButtonsContainerSx = (mode: "light" | "dark") => ({
  display: "flex",
  justifyContent: "space-around",
  gap: 1,
  padding: "8px 12px",
  borderTop: mode === "dark" ? "1px solid #3a3a3a" : "1px solid #eee",
});

export const checkboxSelectorSelectAllButtonSx = {
  fontFamily: "montserrat",
  fontSize: "12px",
  fontWeight: 500,
  textTransform: "none" as const,
  color: "#0FB0C2",
  padding: "4px 8px",
  "&:hover": {
    backgroundColor: "rgba(15, 176, 194, 0.1)",
    color: "#2FC8B8",
  },
};

export const checkboxSelectorClearAllButtonSx = {
  fontFamily: "montserrat",
  fontSize: "12px",
  fontWeight: 500,
  textTransform: "none" as const,
  color: "#FF4262",
  padding: "4px 8px",
  "&:hover": {
    backgroundColor: "rgba(255, 66, 98, 0.1)",
    color: "#BF4262",
  },
};

export const checkboxSelectorMenuItemsContainerSx = (mode: "light" | "dark") => ({
  maxHeight: "350px",
  overflowY: "auto" as const,
  overflowX: "hidden" as const,
  scrollBehavior: "smooth" as const,
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: mode === "dark" ? "#1a1a1a" : "#f5f5f5",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: mode === "dark" ? "#555555" : "#bdbdbd",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: mode === "dark" ? "#777777" : "#9e9e9e",
    },
  },
});

export const fileUploadText = {
  marginTop: spaces[0],
  fontWeight: "bold",
  color: colors.black,
};

export const fileUploadSubtext = {
  color: colors.textSecondary,
  marginTop: spaces[0],
  fontSize: "12px",
  fontWeight: "600",
};

export const fileUploadButtonBox = {
  ...flexRow,
  justifyContent: "space-between",
  gap: spaces[0],
};

export const cameraShotButton = {
  color: colors.primary,
};

export const cameraShotDialogTitle = {
  fontFamily: "montserrat",
  fontWeight: 600,
};

export const cameraShotDialogContent = {
  display: "flex",
  flexDirection: "column",
  gap: spaces[1],
};

export const cameraShotPreviewFrame = {
  width: "100%",
  overflow: "hidden",
  borderRadius: "4px",
  backgroundColor: colors.black,
};

export const cameraShotMedia = {
  width: "100%",
  display: "block",
};

export const cameraShotUnavailable = {
  minHeight: "180px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: spaces[2],
  textAlign: "center" as const,
  color: colors.textSecondary,
  backgroundColor: colors.lightGray,
};

export const cameraShotFallbackButton = {
  alignSelf: "center",
  textTransform: "none" as const,
};

export const cameraShotDialogActions = {
  flexWrap: "wrap" as const,
  padding: spaces[1],
};

export const cameraShotInputOptions = {
  display: "flex",
  flexDirection: "column",
  gap: spaces[1],
};

export const cameraShotCameraAction = {
  order: 2,
  alignSelf: "center",
  "@media (max-width: 600px)": {
    order: 1,
  },
};

export const cameraShotFileAction = {
  order: 1,
  "@media (max-width: 600px)": {
    order: 2,
  },
};

// =========================
// Notification menu
// =========================

export const notificationFooterContainer = {
  ...flexColumnCenter,
  padding: `${spaces[1]} ${spaces[1.5]}`,
  borderTop: `1px solid ${colors.lightGray}`,
  width: "100%",
};

export const markAllAsViewedLinkButton = {
  ...{
    fontFamily: "montserrat",
    fontSize: "13px",
    fontWeight: "500",
    color: colors.bluePrimary,
  },
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: `${spaces[0.5]} 0`,
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 0.8,
    textDecoration: "underline",
  },
  "&:disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

// =========================
// Chart export helpers (PDF/Canvas)
// =========================

export const pdfDownloadButton = {
  position: 'absolute',
  top: spaces[0.5],
  right: spaces[0.5],
  zIndex: zIndexes.z1205,
  minWidth: 'auto',
  padding: spaces[1],
  backgroundColor: colors.whiteOpacity90,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: colors.whiteOpacity100,
  },
};

// =========================
// Dashboards list helpers
// =========================

export const dashboardListItem = {
  display: "flex",
  border: `1px solid ${colors.border}`,
  borderRadius: 4,
  marginBottom: spaces[1],
};

export const dashboardListSecondaryRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: spaces[1],
};

// =========================
// TextField character counter
// =========================

export const textFieldCharCounterBox = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "2px",
  paddingRight: "2px",
};

export const textFieldCharCounterText = {
  fontSize: "11px",
  fontFamily: "montserrat",
  fontWeight: "400",
  lineHeight: 1,
};
